<template>
  <div :class="'base-create ' + (currentStep > componentStep ? 'create-class-com-disable' : '')">
    <Header :addLearnerStep="componentStep" :title="title"></Header>
    <div class="base-create-body">
      <licenses-avai :headAvaiMemberships="headAvaiMemberships" />
      <el-form class="auth-form" :model="form" :rules="rules" ref="form">
        <div class="form-row">
          <el-col :span="shouldShowInviteAll ? 14 : 24">
            <span class="select-license"> {{ $t("organizations.invite_users.click_to_assign") }} </span>
            <primary-button @click.native="handleAssignLicense">
              {{ $t("organizations.invite_users.assign_all") }}
            </primary-button>
            <tertiary-button @click.native="handleClearAssignLicense">
              {{ $t("organizations.invite_users.clear_all") }}
            </tertiary-button>
          </el-col>
          <el-col v-if="shouldShowInviteAll && isElsaSpeakProduct" :span="10">
            <div class="select-team-wrapper">
              <span class="select-team-label">
                {{ $t($i18nLabelsHelper(currentOrganizationType, "modals.invite_users.assign_all")) }}
              </span>
              <elsa-select
                class="select-team"
                name="selectedTeam"
                :placeholder="
                  $t($i18nLabelsHelper(currentOrganizationType, 'modals.invite_users.select_group_placeholder'))
                "
                :options="options"
                :no-data-text="$t($i18nLabelsHelper(currentOrganizationType, 'modals.invite_users.no_group_available'))"
                v-model.lazy="selectedTeamIn"
                @change="handleAssignTeamAll()"
              />
            </div>
          </el-col>
        </div>
        <el-table :data="users" style="width: 100%" class="no-pointer">
          <el-table-column
            prop="text"
            :label="
              isOrgUseDummyEmail
                ? $t('dummy_learner.dummy_label')
                : $t('organizations.invite_users.assign_license.table.email')
            "
            width="300"
          >
            <template slot-scope="scope">
              <div class="owner-info">
                <p>{{ scope.row.text }}</p>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="text"
            :label="$t('organizations.invite_users.assign_license.table.first_name')"
            width="150"
          >
            <template slot-scope="scope">
              <div class="owner-info">
                <p>{{ scope.row.first_name }}</p>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="text"
            :label="$t('organizations.invite_users.assign_license.table.last_name')"
            width="150"
          >
            <template slot-scope="scope">
              <div class="owner-info">
                <p>{{ scope.row.last_name }}</p>
              </div>
            </template>
          </el-table-column>
          <slot name="select_team"></slot>
          <el-table-column prop="text" :label="$t('organizations.invite_users.assign_license.table.license')">
            <template slot-scope="scope">
              <elsa-select
                class="select-membership-single"
                name="selectedMembership"
                :options="avaiMemberships"
                no-data-text="No plan available"
                @change="changeMembership(scope.row)"
                v-model.lazy="scope.row.plan_id"
              />
            </template>
          </el-table-column>
        </el-table>
      </el-form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { pathOr } from "ramda";
import { selectProMemberships } from "@/constants/memberships";
import ElsaSelect from "@/components/common/Select";
import axios from "@/utils/axios";
import { successMessage, warningMessage } from "@/utils/messages";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import { PLAN_MONTH_DURATION } from "@/constants/generic";
import { addMonths } from "date-fns";
import { formatDate } from "@/utils/formatDate";
import Header from "@/components/common/add-learners/Header";
import LicensesAvai from "@/components/common/add-learners/LicensesAvai";
import { getPlanLabel } from "@/helpers/functions";

function initialState() {
  return {
    form: {},
    rules: {},
    orgPlanDetails: [],
  };
}

export default {
  components: {
    ElsaSelect,
    PrimaryButton,
    TertiaryButton,
    Header,
    LicensesAvai,
  },
  data() {
    return {
      ...initialState(),
      selectedTeamIn: this.selectedTeam,
    };
  },

  computed: {
    ...mapGetters({
      classes: "classes/getClassList",
      currentOrganizationType: "organization/currentOrganizationType",
      currentOrganizationId: "organization/currentOrganizationId",
      currentOrganization: "organization/currentOrganization",
      isOrgUseDummyEmail: "organization/isOrganizationUseDummyEmail",
      isElsaSpeakProduct: "organization/isElsaSpeakProduct",
      isNovaOrg: "organization/isNovaOrg",
    }),
    title() {
      return this.$t("organizations.invite_users.assign_license.title");
    },
    sortedOrgPlans() {
      const plans = [...this.orgPlanDetails];
      const sortedData = plans.sort((a, b) => a.id - b.id); // sort by id from earliest to latest
      const data = [];

      sortedData.forEach(p => {
        // no book yet
        const membership = selectProMemberships.find(e => e.value == p.package);
        if (!membership || !membership.label) return;

        const expire_date_label = p.expire_date ? " - exp. on " + p.expire_date : "";
        const plan_name = getPlanLabel(p.product_type, p.package);
        const plan_label = p.qty - p.qty_assigned + " " + plan_name + expire_date_label;
        data.push({ ...p, plan_label, plan_name, expire_date_label });
      });

      data.sort((a, b) => {
        if (a.expire_date && !b.expire_date) {
          return -1; // a has expire_date, b does not, so a should come first
        } else if (!a.expire_date && b.expire_date) {
          return 1; // b has expire_date, a does not, so b should come first
        } else if (a.expire_date && b.expire_date) {
          return new Date(a.expire_date) - new Date(b.expire_date); // sort by expire_date from earliest to latest
        } else {
          return a.plan_name.localeCompare(b.plan_name); // sort alphabetically by plan_name
        }
      });

      return data;
    },
    headAvaiMemberships() {
      return this.sortedOrgPlans;
    },
    avaiMemberships() {
      const data = [];

      this.sortedOrgPlans.forEach(plan => {
        let newPlan = {
          label: plan.plan_name + plan.expire_date_label,
          value: plan.id,
        };

        if (plan.qty <= plan.qty_assigned) {
          newPlan["disabled"] = true;
        }

        data.push(newPlan);
      });

      data.push({
        label: this.$t("classes.create_class_flow.table.invite_only"),
      });

      return data;
    },
    options() {
      return this.availableClasses.map(c => {
        if (!c.value) {
          c.label = this.$t(
            this.$i18nLabelsHelper(this.currentOrganizationType, "modals.invite_users.select_group_placeholder")
          );
        }
        return c;
      });
    },
    users() {
      return pathOr([], ["assignmentLicense", "users"])(this);
    },
  },
  props: {
    currentStep: {
      type: Number,
      default: 4,
    },
    componentStep: {
      type: Number,
      default: 4,
    },
    assignmentLicense: {
      type: Object,
    },
    availableClasses: {
      type: Array,
      default: () => [],
    },
    selectedTeam: {
      type: Number,
    },
    shouldShowInviteAll: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    resetModal() {
      Object.assign(this.$data, initialState());
    },
    changeMembership(membershipSelected, user) {
      this.orgPlanDetails.forEach(plan => {
        const qty_assigned_list = this.users.filter(u => u.plan_id == plan.id);
        plan["qty_assigned"] = qty_assigned_list.length;
      });
      this.updateAssignLicense({
        avaiMemberships: this.avaiMemberships,
      });
    },
    ...mapActions("classes", ["getClasses"]),
    getOrganizationContracts() {
      let url = `v1/checkouts/${this.currentOrganizationId}/payments`;
      return axios.get(url).then(response => {
        const payments = response.data;
        if (payments.length > 0) {
          payments.forEach(payment => {
            let plan_details = payment.plan_details;
            plan_details.forEach(plan => {
              const qty_assigned_list = this.users.filter(u => u.plan_id == plan.id);
              const nowTime = new Date().getTime();
              let validTime = null;
              if (plan.auto_active_license_date && plan.is_subscription) {
                validTime = this.expiredAtForAutoActive(plan.auto_active_license_date, plan.package);
              }
              // check remaining license and not expired yet, if payment offline validtime = null
              if (plan.number_of_learner > plan.number_of_license_used && (validTime >= nowTime || !validTime)) {
                const newPlan = {
                  id: plan.id,
                  package: plan.package,
                  qty: plan.number_of_learner - plan.number_of_license_used,
                  qty_assigned: qty_assigned_list.length,
                  expire_date: validTime
                    ? formatDate(validTime, this.currentOrganization.timezone, "MMM dd, yyyy")
                    : null,
                  product_type: plan.product_types[0],
                };
                this.orgPlanDetails.push(newPlan);
              }
            });
          });
          if (this.isNovaOrg) {
            this.handleAssignLicense();
          }
        }
      });
    },
    handleAssignLicense() {
      const newEmails = [];
      let planIdx = 0;
      this.users.forEach(user => {
        const plan = this.orgPlanDetails[planIdx];
        if (plan && plan["qty"] == plan["qty_assigned"]) {
          planIdx = planIdx + 1;
        }
        if (planIdx < this.orgPlanDetails.length && plan["qty"] > plan["qty_assigned"]) {
          const newEmail = { ...user, plan_id: plan.id };
          plan["qty_assigned"] = plan["qty_assigned"] + 1;
          newEmails.push(newEmail);
        } else {
          const newEmail = { ...user };
          newEmails.push(newEmail);
        }
      });
      this.updateAssignLicense({
        users: newEmails,
        avaiMemberships: this.avaiMemberships,
      });
    },
    handleClearAssignLicense() {
      const newEmails = [];
      this.users.forEach(user => {
        const newEmail = { ...user, plan_id: null };
        newEmails.push(newEmail);
      });
      this.orgPlanDetails.forEach(plan => {
        plan["qty_assigned"] = 0;
      });
      this.updateAssignLicense({
        users: newEmails,
        avaiMemberships: this.avaiMemberships,
      });
    },
    expiredAtForAutoActive(date_auto_active, membership) {
      const invalidDate = addMonths(new Date(date_auto_active), PLAN_MONTH_DURATION[membership]);
      return invalidDate;
    },
    handleClickContinute() {
      this.$emit("next-step", this.currentStep);
    },
    updateAssignLicense(klass) {
      this.$emit("update-assign-license", klass);
    },
    handleAssignTeamAll() {
      for (let i = 0; i < this.users.length; i++) {
        this.users[i].selectedTeam = this.selectedTeamIn;
      }
    },
  },
  mounted() {
    try {
      this.getClasses();
      this.getOrganizationContracts();
      if (this.selectedTeamIn) this.handleAssignTeamAll();
    } catch {
      return warningMessage(this.$t("common.generic_error_message"))(this);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../scss/_colors.scss";
p {
  font-weight: 300;
  font-size: 18px;
  color: $dark-blue-2;
  margin-bottom: 32px;
}
.el-row {
  flex-wrap: wrap;
}
.form-row {
  display: flex;
  margin-bottom: 32px;
  .select-license {
    padding-top: 16px;
    margin-right: 32px;
  }
  .elsa-primary-button {
    margin-right: 24px;
  }
}
.select-membership-single {
  margin-bottom: 0px !important;
}
.el-form {
  padding-bottom: 0;
}
.select-team-wrapper {
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: row;
}
.select-team-label {
  flex-grow: 1;
  flex-shrink: 0;
  margin-right: 16px;
  font-weight: bold;
  font-size: 18px;
  color: $black;
}
.el-form-item.select-team {
  width: 100%;
  margin-bottom: 0;
}
</style>
